import React, { useState, useEffect, useCallback } from 'react';
import { Database, Shield, FileText, Plus, Trash2, Loader2, AlertTriangle, Clock, Bell, X, ChevronDown, ChevronRight, Lock, Unlock, Eye, Send, CheckCircle, XCircle } from 'lucide-react';
import { API } from '../../App';

/* ── helpers ─────────────────────────────────────────────────────── */
const SEVERITY_COLORS = { critical: '#A61E17', high: '#FF4D00', medium: '#F2A900', low: '#00D4FF' };
const severityBadge = (sev) => (
  <span className="px-2 py-0.5 rounded text-xs font-mono font-bold uppercase" style={{ backgroundColor: SEVERITY_COLORS[sev] || '#2A2A2A', color: sev === 'medium' ? '#0A0A0A' : '#F5F1E8' }}>
    {sev}
  </span>
);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const Card = ({ children, className = '' }) => <div className={`bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 ${className}`}>{children}</div>;
const Input = (props) => <input {...props} className={`bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none ${props.className || ''}`} />;
const Select = ({ children, ...props }) => <select {...props} className={`bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none ${props.className || ''}`}>{children}</select>;
const Btn = ({ children, variant = 'primary', ...props }) => {
  const base = variant === 'primary'
    ? 'bg-[#FF4D00] text-[#0A0A0A] font-bold hover:bg-[#E63E00]'
    : variant === 'danger'
      ? 'bg-[#A61E17] text-[#F5F1E8] font-bold hover:bg-[#8B1812]'
      : 'bg-[#2A2A2A] text-[#F5F1E8] hover:bg-[#333]';
  return <button {...props} className={`px-4 py-2 text-sm rounded flex items-center gap-2 disabled:opacity-50 ${base} ${props.className || ''}`}>{children}</button>;
};
const Spinner = () => <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#FF4D00]" /></div>;

/* ── Retention & Legal Holds ─────────────────────────────────────── */
function RetentionSection({ workspaceId }) {
  const [policies, setPolicies] = useState([]);
  const [holds, setHolds] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHoldForm, setShowHoldForm] = useState(false);
  const [form, setForm] = useState({ data_type: '', retention_days: '', action: 'archive' });
  const [holdForm, setHoldForm] = useState({ reason: '', scope: 'workspace', custodian_id: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get('/api/enterprise/retention/policies', { params: { workspace_id: workspaceId } }).catch(() => ({ data: { policies: [] } })),
      API.get('/api/enterprise/legal-holds', { params: { workspace_id: workspaceId } }).catch(() => ({ data: { holds: [] } })),
      API.get('/api/enterprise/retention/viewer', { params: { workspace_id: workspaceId } }).catch(() => ({ data: null })),
      API.get('/api/enterprise/retention/certifications').catch(() => ({ data: { certifications: [] } })),
    ]).then(([pRes, hRes, vRes, cRes]) => {
      setPolicies(pRes.data.policies || pRes.data || []);
      setHolds(hRes.data.holds || hRes.data || []);
      setViewer(vRes.data);
      setCerts(cRes.data.certifications || cRes.data || []);
    }).finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    if (!form.data_type || !form.retention_days) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/retention/policies', { workspace_id: workspaceId, data_type: form.data_type, retention_days: parseInt(form.retention_days), action: form.action });
      setForm({ data_type: '', retention_days: '', action: 'archive' });
      setShowForm(false);
      load();
    } catch {}
    setSaving(false);
  };

  const deletePolicy = async (id) => {
    await API.delete(`/api/enterprise/retention/policies/${id}`);
    load();
  };

  const placeHold = async () => {
    if (!holdForm.reason) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/legal-holds', { workspace_id: workspaceId, reason: holdForm.reason, scope: holdForm.scope, custodian_id: holdForm.custodian_id || undefined });
      setHoldForm({ reason: '', scope: 'workspace', custodian_id: '' });
      setShowHoldForm(false);
      load();
    } catch {}
    setSaving(false);
  };

  const releaseHold = async (id) => {
    await API.post(`/api/enterprise/legal-holds/${id}/release`);
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Retention Policies */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <Database className="w-4 h-4 text-[#FF4D00]" /> Retention Policies
          </h3>
          <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? <X className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> Add Policy</>}
          </Btn>
        </div>

        {showForm && (
          <Card className="mb-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input placeholder="Data type (e.g. debates)" value={form.data_type} onChange={e => setForm({ ...form, data_type: e.target.value })} />
              <Input placeholder="Retention days" type="number" value={form.retention_days} onChange={e => setForm({ ...form, retention_days: e.target.value })} />
              <Select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })}>
                <option value="archive">Archive</option>
                <option value="delete">Delete</option>
                <option value="anonymize">Anonymize</option>
              </Select>
            </div>
            <Btn onClick={savePolicy} disabled={saving || !form.data_type || !form.retention_days}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Policy'}
            </Btn>
          </Card>
        )}

        {policies.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No retention policies configured</p>
        ) : (
          <div className="space-y-2">
            {policies.map(p => (
              <Card key={p.id} className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-mono text-[#00D4FF]">{p.data_type}</span>
                  <span className="text-xs text-[#A0A0A0]">{p.retention_days} days</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{p.action}</span>
                </div>
                <button onClick={() => deletePolicy(p.id)} className="p-2 text-[#A0A0A0] hover:text-[#A61E17]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Legal Holds */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#F2A900]" /> Legal Holds
          </h3>
          <Btn onClick={() => setShowHoldForm(!showHoldForm)} variant={showHoldForm ? 'ghost' : 'primary'}>
            {showHoldForm ? <X className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> Place Hold</>}
          </Btn>
        </div>

        {showHoldForm && (
          <Card className="mb-3 space-y-3">
            <Input placeholder="Reason for hold" value={holdForm.reason} onChange={e => setHoldForm({ ...holdForm, reason: e.target.value })} className="w-full" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select value={holdForm.scope} onChange={e => setHoldForm({ ...holdForm, scope: e.target.value })}>
                <option value="workspace">Workspace</option>
                <option value="user">User</option>
                <option value="debate">Debate</option>
              </Select>
              <Input placeholder="Custodian ID (optional)" value={holdForm.custodian_id} onChange={e => setHoldForm({ ...holdForm, custodian_id: e.target.value })} />
            </div>
            <Btn onClick={placeHold} disabled={saving || !holdForm.reason}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4" /> Place Hold</>}
            </Btn>
          </Card>
        )}

        {holds.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No active legal holds</p>
        ) : (
          <div className="space-y-2">
            {holds.map(h => (
              <Card key={h.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {h.status === 'active' ? <Lock className="w-3.5 h-3.5 text-[#F2A900]" /> : <Unlock className="w-3.5 h-3.5 text-[#A0A0A0]" />}
                    <span className="text-sm font-mono">{h.reason}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{h.scope}</span>
                  </div>
                  <span className="text-xs text-[#A0A0A0]">Placed {fmtDate(h.created_at)}</span>
                </div>
                {h.status === 'active' && (
                  <Btn onClick={() => releaseHold(h.id)} variant="ghost">
                    <Unlock className="w-4 h-4" /> Release
                  </Btn>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Retention Viewer */}
      {viewer && (
        <div>
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-[#00D4FF]" /> Retention Viewer
          </h3>
          <Card>
            {Array.isArray(viewer.policies || viewer) ? (
              <div className="space-y-2">
                {(viewer.policies || viewer).map((v, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-[#00D4FF]">{v.data_type}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#A0A0A0]">{v.retention_days} days</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{v.action}</span>
                      {v.under_hold && <Lock className="w-3 h-3 text-[#F2A900]" />}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <pre className="text-xs font-mono text-[#A0A0A0] whitespace-pre-wrap">{JSON.stringify(viewer, null, 2)}</pre>
            )}
          </Card>
        </div>
      )}

      {/* Deletion Certifications */}
      {certs.length > 0 && (
        <div>
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-[#22C55E]" /> Deletion Certifications
          </h3>
          <div className="space-y-2">
            {certs.map(c => (
              <Card key={c.id} className="flex items-center justify-between text-sm">
                <span className="font-mono">{c.data_type || c.scope}</span>
                <span className="text-xs text-[#A0A0A0]">{fmtDate(c.certified_at || c.created_at)}</span>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Breach Management ───────────────────────────────────────────── */
function BreachSection({ workspaceId }) {
  const [breaches, setBreaches] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ title: '', severity: 'medium', description: '', affected_data_types: '', estimated_affected_users: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', role: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get('/api/enterprise/breaches', { params: { workspace_id: workspaceId } }).catch(() => ({ data: { breaches: [] } })),
      API.get('/api/enterprise/breaches/overdue').catch(() => ({ data: { breaches: [] } })),
      API.get('/api/enterprise/security-contacts').catch(() => ({ data: { contacts: [] } })),
    ]).then(([bRes, oRes, cRes]) => {
      setBreaches(bRes.data.breaches || bRes.data || []);
      setOverdue(oRes.data.breaches || oRes.data || []);
      setContacts(cRes.data.contacts || cRes.data || []);
    }).finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const createBreach = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/breaches', {
        workspace_id: workspaceId,
        title: form.title,
        severity: form.severity,
        description: form.description,
        affected_data_types: form.affected_data_types ? form.affected_data_types.split(',').map(s => s.trim()) : [],
        estimated_affected_users: form.estimated_affected_users ? parseInt(form.estimated_affected_users) : 0,
      });
      setForm({ title: '', severity: 'medium', description: '', affected_data_types: '', estimated_affected_users: '' });
      setShowForm(false);
      load();
    } catch {}
    setSaving(false);
  };

  const notifyAdmins = async (id) => {
    await API.post(`/api/enterprise/breaches/${id}/notify-admins`);
    load();
  };

  const closeFalsePositive = async (id) => {
    await API.post(`/api/enterprise/breaches/${id}/close-false-positive`);
    load();
  };

  const addContact = async () => {
    if (!contactForm.name || !contactForm.email) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/security-contacts', contactForm);
      setContactForm({ name: '', email: '', role: '' });
      load();
    } catch {}
    setSaving(false);
  };

  const removeContact = async (id) => {
    await API.delete(`/api/enterprise/security-contacts/${id}`);
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Overdue Alerts */}
      {overdue.length > 0 && (
        <Card className="border-[#A61E17] bg-[#A61E17]/10">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#A61E17]" />
            <span className="text-sm font-bold text-[#A61E17]">Overdue Breaches ({overdue.length})</span>
          </div>
          <div className="space-y-1">
            {overdue.map(b => (
              <div key={b.id} className="flex items-center gap-2 text-sm">
                {severityBadge(b.severity)}
                <span className="font-mono">{b.title}</span>
                <span className="text-xs text-[#A0A0A0] ml-auto">{fmtDate(b.created_at)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Breach List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FF4D00]" /> Breach Incidents
          </h3>
          <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? <X className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> Report Breach</>}
          </Btn>
        </div>

        {showForm && (
          <Card className="mb-3 space-y-3">
            <Input placeholder="Breach title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Select>
              <Input placeholder="Affected data types (comma-sep)" value={form.affected_data_types} onChange={e => setForm({ ...form, affected_data_types: e.target.value })} />
              <Input placeholder="Est. affected users" type="number" value={form.estimated_affected_users} onChange={e => setForm({ ...form, estimated_affected_users: e.target.value })} />
            </div>
            <textarea
              placeholder="Description of the breach..."
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none resize-none"
            />
            <Btn onClick={createBreach} disabled={saving || !form.title} variant="danger">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><AlertTriangle className="w-4 h-4" /> Report Breach</>}
            </Btn>
          </Card>
        )}

        {breaches.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No breach incidents recorded</p>
        ) : (
          <div className="space-y-2">
            {breaches.map(b => (
              <Card key={b.id}>
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>
                  {expanded === b.id ? <ChevronDown className="w-4 h-4 text-[#A0A0A0]" /> : <ChevronRight className="w-4 h-4 text-[#A0A0A0]" />}
                  {severityBadge(b.severity)}
                  <span className="text-sm font-mono flex-1">{b.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{b.status || 'open'}</span>
                  <span className="text-xs text-[#A0A0A0]">{fmtDate(b.created_at)}</span>
                </div>
                {expanded === b.id && (
                  <div className="mt-3 pt-3 border-t border-[#2A2A2A] space-y-3">
                    {b.description && <p className="text-sm text-[#A0A0A0]">{b.description}</p>}
                    <div className="flex flex-wrap gap-4 text-xs text-[#A0A0A0]">
                      {b.estimated_affected_users > 0 && <span>Affected users: <span className="text-[#F5F1E8] font-mono">{b.estimated_affected_users}</span></span>}
                      {b.affected_data_types?.length > 0 && (
                        <span>Data types: {b.affected_data_types.map(t => <span key={t} className="ml-1 px-1.5 py-0.5 bg-[#2A2A2A] rounded font-mono">{t}</span>)}</span>
                      )}
                    </div>
                    {b.timeline && b.timeline.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-xs font-bold text-[#A0A0A0] uppercase">Timeline</span>
                        {b.timeline.map((ev, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <Clock className="w-3 h-3 text-[#A0A0A0]" />
                            <span className="text-[#A0A0A0]">{fmtDate(ev.timestamp)}</span>
                            <span className="font-mono">{ev.action || ev.event}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Btn onClick={() => notifyAdmins(b.id)} variant="primary">
                        <Send className="w-3.5 h-3.5" /> Notify Admins
                      </Btn>
                      {b.status !== 'closed' && (
                        <Btn onClick={() => closeFalsePositive(b.id)} variant="ghost">
                          <XCircle className="w-3.5 h-3.5" /> Close as False Positive
                        </Btn>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Security Contacts */}
      <div>
        <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-[#00D4FF]" /> Security Contacts
        </h3>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input placeholder="Name" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} className="flex-1" />
          <Input placeholder="Email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} className="flex-1" />
          <Input placeholder="Role" value={contactForm.role} onChange={e => setContactForm({ ...contactForm, role: e.target.value })} className="w-32" />
          <Btn onClick={addContact} disabled={saving || !contactForm.name || !contactForm.email}>
            <Plus className="w-4 h-4" />
          </Btn>
        </div>
        {contacts.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No security contacts configured</p>
        ) : (
          <div className="space-y-2">
            {contacts.map(c => (
              <Card key={c.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono">{c.name}</span>
                  <span className="text-xs text-[#A0A0A0]">{c.email}</span>
                  {c.role && <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{c.role}</span>}
                </div>
                <button onClick={() => removeContact(c.id)} className="p-2 text-[#A0A0A0] hover:text-[#A61E17]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── DPA & Sub-Processors ────────────────────────────────────────── */
function DpaSection({ workspaceId }) {
  const [dpas, setDpas] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDpaForm, setShowDpaForm] = useState(false);
  const [showProcForm, setShowProcForm] = useState(false);
  const [dpaForm, setDpaForm] = useState({ entity_name: '', status: 'draft', effective_date: '', expiry_date: '' });
  const [procForm, setProcForm] = useState({ name: '', purpose: '', location: '', data_types: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get('/api/enterprise/dpa', { params: { workspace_id: workspaceId } }).catch(() => ({ data: { agreements: [] } })),
      API.get('/api/enterprise/sub-processors').catch(() => ({ data: { processors: [] } })),
      API.get('/api/enterprise/sub-processors/notifications').catch(() => ({ data: { notifications: [] } })),
    ]).then(([dRes, pRes, nRes]) => {
      setDpas(dRes.data.agreements || dRes.data || []);
      setProcessors(pRes.data.processors || pRes.data || []);
      setNotifications(nRes.data.notifications || nRes.data || []);
    }).finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const createDpa = async () => {
    if (!dpaForm.entity_name) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/dpa', { workspace_id: workspaceId, ...dpaForm });
      setDpaForm({ entity_name: '', status: 'draft', effective_date: '', expiry_date: '' });
      setShowDpaForm(false);
      load();
    } catch {}
    setSaving(false);
  };

  const addProcessor = async () => {
    if (!procForm.name) return;
    setSaving(true);
    try {
      await API.post('/api/enterprise/sub-processors', {
        ...procForm,
        data_types: procForm.data_types ? procForm.data_types.split(',').map(s => s.trim()) : [],
      });
      setProcForm({ name: '', purpose: '', location: '', data_types: '' });
      setShowProcForm(false);
      load();
    } catch {}
    setSaving(false);
  };

  const removeProcessor = async (id) => {
    await API.delete(`/api/enterprise/sub-processors/${id}`);
    load();
  };

  const submitObjection = async (id) => {
    await API.post(`/api/enterprise/sub-processors/notifications/${id}/object`);
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {notifications.length > 0 && (
        <Card className="border-[#F2A900] bg-[#F2A900]/5">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-[#F2A900]" />
            <span className="text-sm font-bold text-[#F2A900]">Sub-Processor Change Notifications ({notifications.length})</span>
          </div>
          <div className="space-y-2">
            {notifications.map(n => (
              <div key={n.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono">{n.processor_name || n.title}</span>
                  <span className="text-xs text-[#A0A0A0] ml-2">{n.change_type || n.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#A0A0A0]">{fmtDate(n.created_at)}</span>
                  {n.status !== 'objected' && (
                    <Btn onClick={() => submitObjection(n.id)} variant="ghost" className="text-xs py-1 px-2">
                      Object
                    </Btn>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* DPA Agreements */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#FF4D00]" /> DPA Agreements
          </h3>
          <Btn onClick={() => setShowDpaForm(!showDpaForm)} variant={showDpaForm ? 'ghost' : 'primary'}>
            {showDpaForm ? <X className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> New DPA</>}
          </Btn>
        </div>

        {showDpaForm && (
          <Card className="mb-3 space-y-3">
            <Input placeholder="Entity name" value={dpaForm.entity_name} onChange={e => setDpaForm({ ...dpaForm, entity_name: e.target.value })} className="w-full" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={dpaForm.status} onChange={e => setDpaForm({ ...dpaForm, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </Select>
              <Input type="date" placeholder="Effective date" value={dpaForm.effective_date} onChange={e => setDpaForm({ ...dpaForm, effective_date: e.target.value })} />
              <Input type="date" placeholder="Expiry date" value={dpaForm.expiry_date} onChange={e => setDpaForm({ ...dpaForm, expiry_date: e.target.value })} />
            </div>
            <Btn onClick={createDpa} disabled={saving || !dpaForm.entity_name}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create DPA'}
            </Btn>
          </Card>
        )}

        {dpas.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No DPA agreements</p>
        ) : (
          <div className="space-y-2">
            {dpas.map(d => (
              <Card key={d.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-[#A0A0A0]" />
                  <span className="text-sm font-mono">{d.entity_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-mono ${d.status === 'active' ? 'bg-[#22C55E]/20 text-[#22C55E]' : d.status === 'expired' ? 'bg-[#A61E17]/20 text-[#A61E17]' : 'bg-[#2A2A2A] text-[#A0A0A0]'}`}>
                    {d.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#A0A0A0]">
                  {d.effective_date && <span>{fmtDate(d.effective_date)}</span>}
                  {d.expiry_date && <span>— {fmtDate(d.expiry_date)}</span>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Sub-Processors */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#00D4FF]" /> Sub-Processor Register
          </h3>
          <Btn onClick={() => setShowProcForm(!showProcForm)} variant={showProcForm ? 'ghost' : 'primary'}>
            {showProcForm ? <X className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> Add Processor</>}
          </Btn>
        </div>

        {showProcForm && (
          <Card className="mb-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Processor name" value={procForm.name} onChange={e => setProcForm({ ...procForm, name: e.target.value })} />
              <Input placeholder="Purpose" value={procForm.purpose} onChange={e => setProcForm({ ...procForm, purpose: e.target.value })} />
              <Input placeholder="Location" value={procForm.location} onChange={e => setProcForm({ ...procForm, location: e.target.value })} />
              <Input placeholder="Data types (comma-sep)" value={procForm.data_types} onChange={e => setProcForm({ ...procForm, data_types: e.target.value })} />
            </div>
            <Btn onClick={addProcessor} disabled={saving || !procForm.name}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Sub-Processor'}
            </Btn>
          </Card>
        )}

        {processors.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] text-center py-4">No sub-processors registered</p>
        ) : (
          <div className="space-y-2">
            {processors.map(p => (
              <Card key={p.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-mono">{p.name}</span>
                    {p.location && <span className="text-xs px-2 py-0.5 rounded bg-[#2A2A2A] font-mono">{p.location}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {p.purpose && <span className="text-xs text-[#A0A0A0]">{p.purpose}</span>}
                    {p.data_types?.length > 0 && p.data_types.map(t => (
                      <span key={t} className="text-xs px-1.5 py-0.5 bg-[#2A2A2A] rounded font-mono">{t}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => removeProcessor(p.id)} className="p-2 text-[#A0A0A0] hover:text-[#A61E17]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────── */
const SUB_TABS = [
  { id: 'retention', label: 'Retention & Legal Holds', icon: Database },
  { id: 'breaches', label: 'Breach Management', icon: AlertTriangle },
  { id: 'dpa', label: 'DPA & Sub-Processors', icon: FileText },
];

export default function DataGovernanceSection({ workspaceId }) {
  const [subTab, setSubTab] = useState('retention');

  return (
    <div className="space-y-4">
      <div className="flex gap-1 overflow-x-auto pb-1">
        {SUB_TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono whitespace-nowrap rounded transition-colors ${
                subTab === t.id
                  ? 'bg-[#FF4D00] text-[#0A0A0A] font-bold'
                  : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8] hover:border-[#FF4D00]/30'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === 'retention' && <RetentionSection workspaceId={workspaceId} />}
      {subTab === 'breaches' && <BreachSection workspaceId={workspaceId} />}
      {subTab === 'dpa' && <DpaSection workspaceId={workspaceId} />}
    </div>
  );
}
